import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import paramsData from '../parameters.json';
import { Button } from 'primereact/button';
import { VikingMaltCalculator } from '../interfaces';
import IConstantProps = VikingMaltCalculator.IConstantProps;
import IParameterProperties = VikingMaltCalculator.IParameterProperties;
import IParametersData = VikingMaltCalculator.IParametersData;
import { MultiSelect } from 'primereact/multiselect';

interface IParametersTableProps {
    parameters: string[];
    setParametersData: Function;
    setSelectedParameters: Function;
}

const VARIETIES = [
    { value: 'Amidala' },
    { value: 'Avalon' },
    { value: 'Dekan' },
    { value: 'Elinor' },
    { value: 'Fantex' },
    { value: 'Grace' },
    { value: 'Irina' },
    { value: 'Laureate' },
    { value: 'Michelle' },
    { value: 'Overture' },
    { value: 'Planet' },
    { value: 'Propino' },
    { value: 'Prospect' },
    { value: 'Quench' },
    { value: 'Sebastian' },
    { value: 'Tarvete' },
    { value: 'Tipple' },
];

const FIRST_YEAR = 2020;

const DEFAULT_CONSTANTS = { variety: '', cropYear: '', resultCount: 5 };

function VikingMaltParametersTable(props: IParametersTableProps) {
    let parameters = props.parameters.map((param) => {
        const sessionParameters: Array<object> = JSON.parse(sessionStorage.getItem('modifiedParameters'));
        if (sessionParameters) {
            const currentParameter = sessionParameters.filter((e: any) => e.id === param);
            if (currentParameter[0]) {
                return { ...currentParameter[0] };
            }
        }
        return { id: param, sp: null, min: null, max: null, weight: 1 };
    });

    const [constants, setConstants] = useState<VikingMaltCalculator.IConstantProps>();

    const cropYearOptions = [];
    let year = FIRST_YEAR;
    const currentDate = new Date();
    while (year <= currentDate.getFullYear()) {
        const option = { value: '' + year };
        if (year !== currentDate.getFullYear()) {
            cropYearOptions.push(option);
        }

        if (year === currentDate.getFullYear() && currentDate.getMonth() + 1 >= 6) {
            cropYearOptions.push(option);
        }
        year++;
    }

    const constantsSelectionHandler = (e: any) => {
        setConstantsPropertyState(e.target.id as keyof IConstantProps, e.target.value);
    };

    const resultInputHandler = (e) => {
        const tempValue = e.target.value ?? 5;
        setConstantsPropertyState(e.target.id as keyof IConstantProps, tempValue);
    };

    const setConstantsPropertyState = (property: keyof IConstantProps, value: any) => {
        setConstants((prevState) => ({ ...prevState, [property]: value }));
    };

    const deleteClickHandler = (e: any) => {
        const selectedParameters = JSON.parse(sessionStorage.getItem('selectedParameters'));
        const modifiedParameters = JSON.parse(sessionStorage.getItem('modifiedParameters'));
        if (e.target.id.length > 0) {
            const newSelectedParameters = selectedParameters.filter((element: string) => element !== e.target.id);
            const newModifiedParameters = modifiedParameters.filter((element: IParameterProperties) => element.id !== e.target.id);

            sessionStorage.setItem('selectedParameters', JSON.stringify(newSelectedParameters));
            sessionStorage.setItem('modifiedParameters', JSON.stringify(newModifiedParameters));

            props.setParametersData((prevState: IParametersData) => ({ ...prevState, parameters: newModifiedParameters }));
            props.setSelectedParameters(newSelectedParameters);
        } else {
            const newSelectedParameters = selectedParameters.filter((element: string) => element !== e.target.offsetParent.id);
            const newModifiedParameters = modifiedParameters.filter((element: IParameterProperties) => element.id !== e.target.offsetParent.id);

            sessionStorage.setItem('selectedParameters', JSON.stringify(newSelectedParameters));
            sessionStorage.setItem('modifiedParameters', JSON.stringify(newModifiedParameters));

            props.setParametersData((prevState: IParametersData) => ({ ...prevState, parameters: newModifiedParameters }));
            props.setSelectedParameters(newSelectedParameters);
        }
    };

    useEffect(() => {
        const sessionConstants = sessionStorage.getItem('constants');
        const sessionModifiedParameters = sessionStorage.getItem('modifiedParameters');

        if (sessionConstants) {
            setConstants(JSON.parse(sessionConstants));
            props.setParametersData((prevState: IParametersData) => ({ ...prevState, constants: JSON.parse(sessionConstants) }));
        } else {
            setConstants(DEFAULT_CONSTANTS);
            props.setParametersData((prevState: IParametersData) => ({ ...prevState, constants: DEFAULT_CONSTANTS }));
        }

        if (sessionModifiedParameters) {
            props.setParametersData((prevState: IParametersData) => ({ ...prevState, parameters: JSON.parse(sessionModifiedParameters) }));
        }
    }, []);

    useEffect(() => {
        if (constants) {
            sessionStorage.setItem('constants', JSON.stringify(constants));
            props.setParametersData((prevState: IParametersData) => ({ ...prevState, constants: constants }));
        }
    }, [constants]);

    const onCellEditComplete = (e: any) => {
        let { rowData, newValue, field, originalEvent: event } = e;
        switch (field) {
            case 'sp':
            case 'min':
            case 'max':
            case 'weight':
                rowData[field] = newValue;
                sessionStorage.setItem('modifiedParameters', JSON.stringify(parameters));
                props.setParametersData((prevState: IParametersData) => ({ ...prevState, parameters: parameters }));
                break;
            default:
                event.preventDefault();
                break;
        }
    };

    const numberEditor = (options: any) => {
        const currentElement = paramsData.filter((e) => e.id === options.rowData.id);
        return (
            <InputNumber
                value={options.value}
                inputStyle={{ width: '100%', height: '100%', textAlign: 'center' }}
                onValueChange={(e) => options.editorCallback(e.value)}
                suffix={currentElement[0].unit}
                mode='decimal'
                minFractionDigits={2}
                maxFractionDigits={2}
            />
        );
    };

    const weightEditor = (options: any) => {
        return (
            <InputNumber
                value={options.value}
                inputStyle={{ width: '100%', height: '100%', textAlign: 'center' }}
                onValueChange={(e) => options.editorCallback(e.value)}
                onBlur={e => e.target.value.length === 0 ? options.editorCallback(1) : e.target.value }
                mode='decimal'
                min={0}
                max={1000}
                minFractionDigits={2}
                maxFractionDigits={2}
            />
        );
    };

    const idBodyTemplate = (options: IParameterProperties) => {
        const currentElement = paramsData.filter((e) => e.id === options.id);
        return `${currentElement[0]?.displayName}`;
    };

    const spBodyTemplate = (options: IParameterProperties) => {
        const currentElement = paramsData.filter((e) => e.id === options.id);
        return `${options.sp !== null ? `${options.sp}${currentElement[0]?.unit ? `${currentElement[0]?.unit}` : ''}` : ''}`;
    };

    const minBodyTemplate = (options: IParameterProperties) => {
        const currentElement = paramsData.filter((e) => e.id === options.id);
        return `${options.min !== null ? `${options.min}${currentElement[0]?.unit ? `${currentElement[0]?.unit}` : ''}` : ''}`;
    };

    const maxBodyTemplate = (options: IParameterProperties) => {
        const currentElement = paramsData.filter((e) => e.id === options.id);
        return `${options.max !== null ? `${options.max}${currentElement[0]?.unit ? `${currentElement[0]?.unit}` : ''}` : ''}`;
    };

    const deleteBodyTemplate = (options: IParameterProperties) => {
        return (
            <Button id={options.id} icon='pi pi-times' iconPos='right' className='bg-transparent text-red-500 border-0 focus:shadow-none' onClick={(e) => deleteClickHandler(e)} />
        );
    };

    return (
        <div className='flex gap-5'>
            <DataTable value={parameters} editMode='cell' tableStyle={{ width: '50rem' }} showGridlines responsiveLayout='stack' breakpoint='600x'>
                <Column field='id' header='Parameter' style={{ width: '40%' }} body={idBodyTemplate}></Column>
                <Column
                    field='sp'
                    header='SP'
                    alignHeader='center'
                    style={{ width: '15%', padding: 0, textAlign: 'center' }}
                    editor={(options) => numberEditor(options)}
                    onCellEditComplete={onCellEditComplete}
                    body={spBodyTemplate}
                ></Column>
                <Column
                    field='min'
                    header='Min'
                    alignHeader='center'
                    style={{ width: '15%', padding: 0, textAlign: 'center' }}
                    editor={(options) => numberEditor(options)}
                    onCellEditComplete={onCellEditComplete}
                    body={minBodyTemplate}
                    min={-99999}
                    max={99999}
                ></Column>
                <Column
                    field='max'
                    header='Max'
                    alignHeader='center'
                    style={{ width: '15%', padding: 0, textAlign: 'center' }}
                    editor={(options) => numberEditor(options)}
                    onCellEditComplete={onCellEditComplete}
                    body={maxBodyTemplate}
                    min={-99999}
                    max={99999}
                ></Column>
                <Column
                    field='weight'
                    header='Weight'
                    alignHeader='center'
                    style={{ width: '15%', padding: 0, textAlign: 'center' }}
                    editor={(options) => weightEditor(options)}
                    onCellEditComplete={onCellEditComplete}
                ></Column>
                <Column field='id' body={deleteBodyTemplate} style={{ width: '10%', padding: 0, textAlign: 'center' }}></Column>
            </DataTable>
            <div className='constants flex flex-column gap-2'>
                <label htmlFor='variety'>Variety</label>
                <MultiSelect
                    id='variety'
                    style={{ maxWidth: '150px' }}
                    placeholder='Select Variety'
                    options={VARIETIES}
                    optionLabel='value'
                    optionValue='value'
                    value={constants?.variety}
                    onChange={constantsSelectionHandler}
                    filter
                />
                <label htmlFor='cropYear'>Crop year</label>
                <MultiSelect
                    id='cropYear'
                    style={{ maxWidth: '150x' }}
                    placeholder='Select Year'
                    options={cropYearOptions}
                    optionLabel='value'
                    optionValue='value'
                    value={constants?.cropYear}
                    onChange={constantsSelectionHandler}
                    filter
                />
                <label htmlFor='resultCount'>Result Count</label>
                <InputNumber id='resultCount' value={constants?.resultCount} onValueChange={resultInputHandler} min={1} max={25} />
            </div>
        </div>
    );
}

export default VikingMaltParametersTable;
